<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="460"
    content-class="erd-dialog"
    :persistent="sending"
  >
    <div class="erd">
      <!-- Header -->
      <div class="erd__head">
        <div class="erd__head-left">
          <span class="erd__icon">
            <v-icon size="20" color="var(--app-accent)">mdi-email-fast-outline</v-icon>
          </span>
          <div>
            <h3 class="erd__title">Send Email Reminder</h3>
            <p class="erd__subtitle">One reminder email to this applicant</p>
          </div>
        </div>
        <button class="erd__close" :disabled="sending" @click="close">
          <v-icon size="18" color="var(--app-text-muted)">mdi-close</v-icon>
        </button>
      </div>

      <!-- Body -->
      <div class="erd__body">
        <label class="erd__label">Reminder Type</label>
        <er-select
          v-model="selectedReminder"
          :options="reminderOptions"
          width="100%"
        />
        <div class="erd__hint">
          <v-icon size="15" color="var(--app-text-faint)" class="mr-1">
            mdi-information-outline
          </v-icon>
          <span>
            The reminder reuses the client's confirmation email subject and
            recipients. It is sent immediately on confirm.
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div class="erd__foot">
        <button class="erd__btn erd__btn--ghost" :disabled="sending" @click="close">
          Cancel
        </button>
        <button
          class="erd__btn erd__btn--primary"
          :disabled="sending || !selectedReminder"
          @click="send"
        >
          <v-progress-circular
            v-if="sending"
            indeterminate
            size="15"
            width="2"
            color="#fff"
            class="mr-1"
          />
          {{ sending ? "Sending…" : "Send" }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import ErSelect from "./ErSelect.vue";

/**
 * EmailReminderDialog — admin sends a single Grant Application Reminder for
 * one application, from the application detail page.
 * Usage:
 *   <email-reminder-dialog
 *     v-model="dialog"
 *     :application-uuid="uuid"
 *     :service-url="serviceUrl"
 *   />
 */
export default {
  name: "EmailReminderDialog",
  components: { ErSelect },
  props: {
    value: { type: Boolean, default: false },
    applicationUuid: { type: String, default: "" },
    serviceUrl: { type: String, default: "" },
  },
  data: () => ({
    // Reminder type -> backend campaign. The label is what the admin picks.
    reminderTypes: [
      { label: "Grant Application Reminder", campaign: "grant_application" },
      { label: "Form Revert Reminder (Grant Approved)", campaign: "form_revert" },
    ],
    selectedReminder: "Grant Application Reminder",
    sending: false,
  }),
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    reminderOptions() {
      return this.reminderTypes.map((r) => r.label);
    },
    selectedCampaign() {
      const r = this.reminderTypes.find((x) => x.label === this.selectedReminder);
      return r ? r.campaign : "grant_application";
    },
    dialogOpen: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },
  methods: {
    // Close (x / Cancel) — blocked while a send is in progress.
    close() {
      if (this.sending) return;
      this.$emit("input", false);
    },
    toast(message, type) {
      this.$store.dispatch("showMessage", {
        message,
        messageType: type,
        timeout: 4000,
      });
    },
    async send() {
      if (!this.applicationUuid) {
        this.toast("Missing application reference.", "error");
        return;
      }
      this.sending = true;
      try {
        const res = await fetch(
          `${this.serviceUrl}/v1/en/console/email_reminder/send-one`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.auth.token}`,
            },
            body: JSON.stringify({
              applicationUuid: this.applicationUuid,
              campaign: this.selectedCampaign,
            }),
          }
        );
        const result = await res.json();
        if (result.status) {
          this.toast(result.message || "Reminder email sent.", "success");
          this.$emit("input", false);
        } else {
          this.toast(result.message || "Failed to send reminder.", "error");
        }
      } catch (e) {
        console.error(e);
        this.toast("Failed to send reminder.", "error");
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .erd {
  background: var(--app-card);
}

.dark-mode .erd__icon {
  background: var(--app-accent-soft);
}

.dark-mode .erd__title {
  color: var(--app-text);
}

.dark-mode .erd__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .erd__close {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .erd__close:hover {
  background: var(--app-elevated);
}

.dark-mode .erd__label {
  color: var(--app-text-muted);
}

.dark-mode .erd__hint {
  background: var(--app-surface);
  color: var(--app-text-muted);
}

.dark-mode .erd__btn--ghost {
  background: var(--app-card);
  color: var(--app-neutral-fg);
  border-color: var(--app-border);
}

.dark-mode .erd__btn--ghost:hover:not(:disabled) {
  background: var(--app-elevated);
}

/* >>> end auto-dark <<< */

.erd {
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  font-family: "Lato", sans-serif;
}

/* Header */
.erd__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #eef2f6;
}
.erd__head-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.erd__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f0f9ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.erd__title {
  font-size: 0.98rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}
.erd__subtitle {
  font-size: 0.74rem;
  color: #94a3b8;
  margin: 1px 0 0;
}
.erd__close {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}
.erd__close:hover {
  background: #f1f5f9;
}

/* Body */
.erd__body {
  padding: 20px 18px;
}
.erd__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #64748b;
  margin-bottom: 7px;
}
.erd__hint {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  margin-top: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #eef2f6;
  border-radius: 9px;
  font-size: 0.76rem;
  line-height: 1.55;
  color: #64748b;
}

/* Footer */
.erd__foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid #eef2f6;
  background: #fafbfc;
}
.erd__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
}
.erd__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.erd__btn--ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.erd__btn--ghost:hover:not(:disabled) {
  background: #f1f5f9;
}
.erd__btn--primary {
  background: #0ea5e9;
  color: #fff;
}
.erd__btn--primary:hover:not(:disabled) {
  background: #0284c7;
}
</style>
